/**
 * @module reducers (for redux actions)
 *
 * Reducers take the payloads from actions and create new state from them. Each
 * is listening for action-events. More than one reducer can listen to a single
 * action, and react to it. Reducers are responsible for maintaining a
 * consistent "shape" in the store.
 *
 * Don't send whole, complex, objects to reducers. Also, don't store whole,
 * complex, objects directly into the store. Validate all data input and
 * transform complex objects into new shapes by mapping each value to its
 * expected shape.
 *
 * NOTE: We're using Redux-Toolkit, which itself uses ImmerJS under the hood.
 * This allows us to use a sipmler "mutating" syntax when making state updates
 * without actually mutating the state directly. Keep this in mind when writing
 * reducers: mutating state is bad, but we're using a special library to allow
 * us to use a mutating *syntax* to update an *immutable* state ;)
 *
 * @see https://redux-toolkit.js.org/
 * @see https://immerjs.github.io/immer/
 * @see https://immerjs.github.io/immer/update-patterns
 */
import { combineReducers } from '@reduxjs/toolkit';
import account from './account';
import conversations from './conversations';
import * as entities from './entities';
import notifications from './notifications';
import permissions from './permissions';
import reporting from './reporting';
import * as uiDomains from './ui';

// DEPRECATED: remove once new reporting state is fully developed
import legacyOverview from './legacy_overview';
import { benchmarks } from './benchmark';

/**
 * Root Reducer
 *
 * Combine all reducers together and export a single reducer object that is the
 * complete state of the application.
 *
 * @example idea for larger state structure:
 * {
 *   simpleDomainData1: {....},
 *   simpleDomainData2: {....},
 *   entities: { // "related" or "table" data
 *     entityType1: {....},
 *     entityType2: {....}
 *   },
 *   reporting: {
 *     filters: {....},
 *     results: {....},
 *     entities: {....} // dynamically generated entities specific to reporting filters
 *   },
 *   ui: {
 *     domain1: {....},
 *     domain2: {....},
 *     domain3: {....}
 *   }
 * }
 */
const rootReducer = {
  account,
  conversations,
  entities: combineReducers(entities),
  notifications,
  legacyOverview,
  permissions,
  reporting,
  benchmarks,
  ui: combineReducers(uiDomains),
};

export default rootReducer;
